

import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useContext,
} from "react";
import toast from "react-hot-toast";
import LoadingSpinner from "../../Components/CommonComponent/loadingSpiner/LoadingSpinner";
import { IoArrowUndoSharp } from "react-icons/io5";
import { NavLink, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { CommonContext } from "../../App";
import ContactMarquee from "../../Components/CommonComponent/Table/ContactMarquee";
import api from "../../services/api";

//  SubGroup Summary Builder (UPDATED)
const buildSubGroupSummary = (data, category) => {
  const subGroups = {};

  data.forEach((item) => {
    //  FILTER
    const matchGroup =
      item.group_id === (category.groupId || category.group_id);

    const matchType =
      (item.groupType || "").toLowerCase() ===
      (category.groupType || "").toLowerCase();

    if (!matchGroup || !matchType) return;

    const key = item.subGroupName || "Other";

    //  Skip unwanted row
    if (key.toLowerCase() === "difference in opening balance") return;

    if (!subGroups[key]) {
      subGroups[key] = {
        groupType: key, // ⚠️ UI same rakhne ke liye
        groupTypeInHindi: item.subGroupNameInHindi || "",
        opening: 0,
        inQty: 0,
        inAmount: 0,
        outQty: 0,
        outAmount: 0,
        closing: 0,
      };
    }

    // ✅ Aggregate ledger data
    subGroups[key].opening += Number(item.opening_stock || 0);
    subGroups[key].inQty += Number(item.in_side || 0);
    subGroups[key].inAmount += Number(item.total_in_price || 0);
    subGroups[key].outQty += Number(item.out_side || 0);
    subGroups[key].outAmount += Number(item.total_out_price || 0);
  });

  // ✅ Closing calc
  Object.values(subGroups).forEach((g) => {
    g.closing = g.opening + g.inQty - g.outQty;
  });

  return Object.values(subGroups);
};

export default function StockTypeSubGroupTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { branchId, dateRange } = useContext(CommonContext);

  const { category } = location.state || {};

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState(dateRange.startDate);
  const [endDate, setEndDate] = useState(dateRange.endDate);

  const [focusedRowIndex, setFocusedRowIndex] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const containerRef = useRef(null);
  const primeLanguage = localStorage.getItem("selectedLanguage");

  useEffect(() => {
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
  }, [dateRange]);

  // Auto focus
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  // Reset focus
  useEffect(() => {
    setFocusedRowIndex(null);
    setSelectedRows(new Set());
  }, [tableData]);

  // Smooth scroll
  useLayoutEffect(() => {
    if (focusedRowIndex === null) return;

    const container = containerRef.current;
    const row = document.getElementById(`row-${focusedRowIndex}`);

    if (!container || !row) return;

    const containerHeight = container.clientHeight;
    const scrollTop = container.scrollTop;
    const rowTop = row.offsetTop;
    const rowHeight = row.offsetHeight;

    const rowCenter = rowTop + rowHeight / 2;
    const containerCenter = scrollTop + containerHeight / 2;

    const OFFSET = 60;

    if (rowCenter > containerCenter + OFFSET) {
      container.scrollTo({
        top: rowTop - containerHeight / 2 + rowHeight,
        behavior: "smooth",
      });
    } else if (rowCenter < containerCenter - OFFSET) {
      container.scrollTo({
        top: rowTop - containerHeight / 2,
        behavior: "smooth",
      });
    }
  }, [focusedRowIndex]);

  // Keyboard navigation
  const handleTableKeyDown = (e) => {
    if (!tableData.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedRowIndex((prev) =>
        prev === null ? 0 : Math.min(prev + 1, tableData.length - 1)
      );
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedRowIndex((prev) =>
        prev === null ? tableData.length - 1 : Math.max(prev - 1, 0)
      );
    }

    if (e.key === " ") {
      e.preventDefault();
      if (focusedRowIndex === null) return;

      setSelectedRows((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(focusedRowIndex)) newSet.delete(focusedRowIndex);
        else newSet.add(focusedRowIndex);
        return newSet;
      });
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (focusedRowIndex !== null) {
        handleRowClick(tableData[focusedRowIndex]);
      }
    }

    if (e.key === "Escape") {
      e.preventDefault();
      navigate("/inventory-books", { state: { category } });
    }
  };

  // ✅ FETCH DATA
  useEffect(() => {
    if (!branchId || !startDate || !endDate || !category) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const res = await api.get(
          `inventory/get-all-item?branchId=${branchId}&startDate=${startDate}&endDate=${endDate}`
        );

        if (res.data.success) {
          const rawData = Array.isArray(res.data.data)
            ? res.data.data
            : [];

          // ✅ GROUP DIRECTLY
          const summary = buildSubGroupSummary(rawData, category);

          setTableData(summary);
        }
      } catch (err) {
        toast.error("Error fetching stock data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [branchId, startDate, endDate, category]);

  // Row click
  const handleRowClick = (row) => {
    navigate("/inventory-books/inventory-items-table", {
      state: {
        category: {
          ...category,
          subGroupName: row.groupType,
        },
        subGroupFilter: row.groupType,
      },
    });
  };

  // Totals
  const totalOpening = tableData.reduce((s, r) => s + r.opening, 0);
  const totalInQty = tableData.reduce((s, r) => s + r.inQty, 0);
  const totalInAmount = tableData.reduce((s, r) => s + r.inAmount, 0);
  const totalOutQty = tableData.reduce((s, r) => s + r.outQty, 0);
  const totalOutAmount = tableData.reduce((s, r) => s + r.outAmount, 0);
  const totalClosing = tableData.reduce((s, r) => s + r.closing, 0);

  return (
    <div className="container-fluid">
      <div className="row">
        {/* Back Button */}
        <div className="py-1 p-0 mt-2 sticky top-0">
          <NavLink
            to="/inventory-books"
            className="inline-block p-1 px-2 text-theme-backBtn border border-theme-backBtn rounded hover:bg-theme-backBtn hover:text-theme-color"
          >
            <IoArrowUndoSharp />
          </NavLink>
        </div>

        {/* Header */}
        <div className="flex justify-between py-[2px] bg-theme-headerBg text-theme-color px-2 text-sm">
          <div className="font-medium py-1">
            {primeLanguage === "hi"
              ? `${category?.groupNameInHindi  || ""} ${t("subCategoryOf")} `
              : `${t("subCategoryOf")} ${category?.groupName || ""}`}
          </div>
        </div>

        {/* Table */}
        <div
          ref={containerRef}
          tabIndex={0}
          onKeyDown={handleTableKeyDown}
          className="overflow-auto outline-none p-0"
        >
          <table className="min-w-full bg-theme-tableBg text-sm capitalize">
            <thead>
              <tr className="bg-white text-theme-headerBg border-b border-theme-headerBg">
                <th className="px-1 py-[2px] font-medium border-b text-center">{t("srNumber")}</th>
                <th className="px-1 text-start font-medium border-b">{t("subCategoryName")}</th>
                <th className="px-1 text-center font-medium border-b">{t("openingStock")}</th>
                <th className="px-1 text-center font-medium border-b">{t("quantityIn")}</th>
                <th className="px-1 text-center font-medium border-b">{t("amountIn")}</th>
                <th className="px-1 text-center font-medium border-b">{t("quantityOut")}</th>
                <th className="px-1 text-center font-medium border-b">{t("amountOut")}</th>
                <th className="px-1 text-center font-medium border-b">{t("closingStock")}</th>
              </tr>
            </thead>

            {!loading && (
              <>
                <tbody>
                  {tableData.length > 0 ? (
                    tableData.map((row, i) => (
                      <tr
                        key={i}
                        id={`row-${i}`}
                        onClick={() => {
                          setFocusedRowIndex(i);
                          handleRowClick(row);
                        }}
                        className={`cursor-pointer ${
                          focusedRowIndex === i
                            ? "bg-[#5c5b5b] text-white"
                            : selectedRows.has(i)
                            ? "bg-[#f4c48a]"
                            : ""
                        }`}
                      >
                        <td className="text-center border-b px-1">{i + 1}</td>
                        <td className="border-b px-1">
                          <ContactMarquee
                            firstName={row.groupType}
                            secondName={row.groupTypeInHindi}
                          />
                        </td>
                        <td className="text-center border-b px-1">{row.opening}</td>
                        <td className="text-center border-b px-1">{row.inQty}</td>
                        <td className="text-center border-b px-1">
                          {row.inAmount.toLocaleString()}
                        </td>
                        <td className="text-center border-b px-1">{row.outQty}</td>
                        <td className="text-center border-b px-1">
                          {row.outAmount.toLocaleString()}
                        </td>
                        <td className="text-center border-b px-1">{row.closing}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        {t("noDataAvailable")}
                      </td>
                    </tr>
                  )}
                </tbody>

                {tableData.length > 0 && (
                  <tfoot>
                    <tr className="bg-white border-b  border-theme-headerBg">
                      <td colSpan="2" className="font-semibold text-center border-b px-1">
                        {t("total")}
                      </td>
                      <td className="text-center font-semibold border-b px-1">
                        {totalOpening}
                      </td>
                      <td className="text-center font-semibold border-b px-1">
                        {totalInQty}
                      </td>
                      <td className="text-center font-semibold border-b px-1">
                        {totalInAmount.toLocaleString()}
                      </td> 
                      <td className="text-center font-semibold border-b px-1">
                        {totalOutQty}
                      </td>
                      <td className="text-center font-semibold border-b px-1">
                        {totalOutAmount.toLocaleString()}
                      </td>
                      <td className="text-center font-semibold border-b px-1">
                        {totalClosing}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </>
            )}
          </table>
        </div>

        {loading && (
          <div className="flex justify-center my-4">
            <LoadingSpinner size={70} color="#000000" />
          </div>
        )}
      </div>
    </div>
  );
}